// Per-device memory of where the last print from this browser ended, offered
// as a suggestion ("Last sheet ended at #15 → start at #16?").
//
// It is never applied automatically and is not physical-sheet tracking: it only
// knows what this browser last sent to print, and it is ignored as soon as the
// sheet geometry differs from the one it was recorded with.

const STORAGE_KEY = "bw-label-sheet-position-v1";

const getStorage = () => {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch (_error) {
    return null;
  }
};

export const rememberSheetEnd = (
  { geometrySignature, lastPosition, nextFreePosition, slotsPerPage },
  { storage = getStorage(), now = new Date() } = {}
) => {
  if (!storage || !geometrySignature) return;
  try {
    storage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        geometrySignature,
        lastPosition: lastPosition ?? null,
        nextFreePosition: nextFreePosition ?? null,
        slotsPerPage,
        recordedAt: now.toISOString(),
      })
    );
  } catch (_error) {}
};

/** `{ lastPosition, nextFreePosition, recordedAt }` or null when not applicable. */
export const readSheetSuggestion = (geometrySignature, { storage = getStorage() } = {}) => {
  if (!storage || !geometrySignature) return null;
  try {
    const stored = JSON.parse(storage.getItem(STORAGE_KEY) || "null");
    if (!stored || stored.geometrySignature !== geometrySignature) return null;

    const next = Number(stored.nextFreePosition);
    const slots = Number(stored.slotsPerPage);
    // A fully used last sheet (or a bogus value) leaves nothing to continue.
    if (!Number.isInteger(next) || next < 2 || !Number.isInteger(slots) || next > slots) {
      return null;
    }

    return {
      lastPosition: next - 1,
      nextFreePosition: next,
      recordedAt: stored.recordedAt || null,
    };
  } catch (_error) {
    return null;
  }
};

/**
 * True when this browser's last print used a different sheet geometry: a
 * partially used sheet from then does not match the current preset's slots.
 */
export const lastSheetUsedOtherGeometry = (geometrySignature, { storage = getStorage() } = {}) => {
  if (!storage || !geometrySignature) return false;
  try {
    const stored = JSON.parse(storage.getItem(STORAGE_KEY) || "null");
    return Boolean(stored?.geometrySignature) && stored.geometrySignature !== geometrySignature && stored.nextFreePosition != null;
  } catch (_error) {
    return false;
  }
};
